import { useState, useEffect, useRef } from 'react';
import { Search, Shuffle, Filter, ChevronDown, MapPin, Heart, Plus, Wine } from 'lucide-react';
import nhost from '../nhost';
import { avg } from '../helpers';
import WineDetail from './WineDetail';
import WineModal from './WineModal';
import { pushBack, popBack } from '../historyStack';

const WINES_QUERY = `
  query GetWines {
    wines(order_by: { created_at: desc }) {
      id name grape type country region vintage profile profile_source price packaging created_by created_at event_id use_count image_url source_url
      interests { id user_id profile { id display_name avatar_url } }
      session { id host_id food completed wines_visible created_at profile { id display_name } event { event_type event_hosts { profile { id display_name } } } }
      event { id event_type title published }
      ratings { id rating comment user_id profile { display_name } }
      ratings_aggregate { aggregate { count } }
    }
  }
`;

const TYPE = {
  'Rött':        { label: 'Rött',   dot: '#8B2A3E', bg: '#F5E8EB', fg: '#5C1A28' },
  'Vitt':        { label: 'Vitt',   dot: '#A07830', bg: '#F7F0E6', fg: '#7D5A22' },
  'Rosé':        { label: 'Rosé',   dot: '#C4566A', bg: '#FCE8EF', fg: '#A03060' },
  'Mousserande': { label: 'Bubbel', dot: '#356BB3', bg: '#E8F0FC', fg: '#234E8C' },
};

function Stars({ rating, size = 12 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', fontSize: size, letterSpacing: 2, lineHeight: 1 }}>
      <span style={{ color: 'rgba(60,20,30,.18)' }}>★★★★★</span>
      <span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', whiteSpace: 'nowrap', color: 'var(--gold)', width: `${rating / 5 * 100}%` }}>★★★★★</span>
    </span>
  );
}

export default function Wines({ profile, onViewMember, getCached, setCache, invalidateCache, initialSearch = '', initialWineIds = null, onClearMapSearch, initialOpenWineId = null }) {
  const [wines, setWines] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [typeFilter, setTypeFilter] = useState('Alla');
  const [hostFilter, setHostFilter] = useState('Alla');
  const [minRating, setMinRating] = useState(0);
  const [priceRange, setPriceRange] = useState('Alla');
  const [sbFilter, setSbFilter] = useState('Alla');
  const [userFilter, setUserFilter] = useState([]);
  const [search, setSearch] = useState(initialSearch);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('date_desc');
  const [detail, setDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const openedWineIdRef = useRef(null);

  useEffect(() => {
    if (!initialOpenWineId || !wines.length) return;
    if (openedWineIdRef.current === initialOpenWineId) return;
    const wine = wines.find(w => w.id === initialOpenWineId);
    if (wine) {
      openedWineIdRef.current = initialOpenWineId;
      openDetail(wine);
    }
  }, [initialOpenWineId, wines]);

  useEffect(() => {
    fetchWines();
    nhost.graphql.request(`query { profiles(where: { approved: { _eq: true } }) { id display_name } }`)
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.profiles.forEach(p => { map[p.id] = p.display_name; });
          setProfiles(map);
        }
      });
  }, []);

  async function toggleInterest(e, wineId) {
    e.stopPropagation();
    const existing = wines.find(w => w.id === wineId)?.interests?.find(i => i.user_id === profile.id);
    if (existing) {
      await nhost.graphql.request(`mutation { delete_wine_interests_by_pk(id: "${existing.id}") { id } }`);
    } else {
      await nhost.graphql.request(`mutation { insert_wine_interests_one(object: { wine_id: "${wineId}", user_id: "${profile.id}" }) { id } }`);
    }
    invalidateCache?.('wines');
    fetchWines(true);
  }

  async function fetchWines(force = false) {
    const cacheKey = 'wines';
    if (!force) {
      const cached = getCached?.(cacheKey);
      if (cached) { setWines(cached); return; }
    }
    const { data } = await nhost.graphql.request(WINES_QUERY);
    if (data) {
      setWines(data.wines);
      setCache?.(cacheKey, data.wines);
    }
  }

  const visibleWines = wines.filter(w =>
    (!w.session && !w.event_id) ||
    (w.session && (w.session.completed || w.session.wines_visible)) ||
    (w.event_id && w.event?.event_type === 'wine_box' && w.event?.published)
  );

  const isOnSystembolaget = w => !!w.source_url?.toLowerCase().includes('systembolaget');

  const getWineHosts = w => {
    const eh = w.session?.event?.event_hosts?.map(h => h.profile?.display_name).filter(Boolean);
    if (eh?.length) return eh;
    if (w.session?.profile?.display_name) return [w.session.profile.display_name];
    if (w.created_by && profiles[w.created_by]) return [profiles[w.created_by]];
    return [];
  };

  function getWineSource(w) {
    const dateStr = (w.session?.created_at || w.created_at)
      ? new Date(w.session?.created_at || w.created_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' })
      : null;
    if (w.session) {
      const hostNames = getWineHosts(w);
      const isWineBox = w.session.event?.event_type === 'wine_box';
      return { label: isWineBox ? 'Vinlåda' : 'Provning', date: dateStr, addedBy: hostNames };
    }
    if (w.event?.event_type === 'wine_box') {
      return { label: w.event.title || 'Vinlåda', date: dateStr, addedBy: [] };
    }
    const creatorName = w.created_by && profiles[w.created_by] ? [profiles[w.created_by]] : [];
    return { label: 'Löst tillagt', date: dateStr, addedBy: creatorName };
  }

  const hosts = [...new Set(visibleWines.flatMap(getWineHosts))];
  const types = [...new Set(visibleWines.map(w => w.type).filter(Boolean))].sort();

  const usersWithWines = new Set(visibleWines.map(w => w.created_by).filter(Boolean));
  const userProfiles = Object.entries(profiles)
    .filter(([id]) => usersWithWines.has(id))
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'sv'));

  const q = search.toLowerCase().trim();

  const filtered = visibleWines.filter(w => {
    const matchesFilter =
      (typeFilter === 'Alla' || w.type === typeFilter) &&
      (hostFilter === 'Alla' || getWineHosts(w).includes(hostFilter));
    const matchesIds = !initialWineIds || initialWineIds.includes(w.id);
    const matchesSearch = !q || [w.name, w.grape, w.country, w.region, w.vintage?.toString()]
      .some(field => field?.toLowerCase().includes(q));
    const wineAvg = parseFloat(avg(w.ratings)) || 0;
    const matchesRating = minRating === 0 || wineAvg >= minRating;
    const p = w.price;
    const matchesPrice = priceRange === 'Alla' ||
      (priceRange === '<100' && p != null && p < 100) ||
      (priceRange === '100-200' && p != null && p >= 100 && p <= 200) ||
      (priceRange === '200-300' && p != null && p > 200 && p <= 300) ||
      (priceRange === '300+' && p != null && p > 300);
    const matchesUser = userFilter.length === 0 || userFilter.includes(w.created_by);
    const matchesSb = sbFilter === 'Alla' || (sbFilter === 'Ja' ? isOnSystembolaget(w) : !isOnSystembolaget(w));
    return matchesIds && matchesFilter && matchesSearch && matchesRating && matchesPrice && matchesUser && matchesSb;
  });

  const dateKey = w => new Date(w.session?.created_at || w.created_at).getTime();

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'name_asc': return (a.name || '').localeCompare(b.name || '', 'sv');
      case 'price_asc': return (a.price ?? Infinity) - (b.price ?? Infinity);
      case 'price_desc': return (b.price ?? -Infinity) - (a.price ?? -Infinity);
      case 'rating_desc': return (parseFloat(avg(b.ratings)) || 0) - (parseFloat(avg(a.ratings)) || 0);
      case 'rating_asc': return (parseFloat(avg(a.ratings)) || 0) - (parseFloat(avg(b.ratings)) || 0);
      default: return dateKey(b) - dateKey(a);
    }
  });

  const hasAnyPrice = visibleWines.some(w => w.price != null && w.price > 0);

  function openDetail(wine) { pushBack(() => setDetail(null)); setDetail(wine); }
  function closeDetail() { popBack(); setDetail(null); fetchWines(); }

  if (detail) return (
    <WineDetail
      wine={detail}
      wines={visibleWines}
      profile={profile}
      onBack={closeDetail}
      onRefresh={fetchWines}
    />
  );

  const activeCount = (hostFilter !== 'Alla' ? 1 : 0) + (minRating > 0 ? 1 : 0) + (priceRange !== 'Alla' ? 1 : 0) + (sbFilter !== 'Alla' ? 1 : 0) + (userFilter.length > 0 ? 1 : 0) + (sortBy !== 'date_desc' ? 1 : 0);
  const anyFilter = typeFilter !== 'Alla' || activeCount > 0;

  return (
    <div>
      <div className="screen">

        {/* Karta-filter-banner */}
        {initialSearch && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--wine-light)', border: '1px solid rgba(139,42,62,.16)', borderRadius: 12, padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>
            <span>Visar viner från <strong>{initialSearch}</strong></span>
            <button onClick={() => { setSearch(''); onClearMapSearch?.(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)', padding: 0, lineHeight: 1 }}>×</button>
          </div>
        )}

        {/* Sök */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', display: 'flex' }}>
            <Search size={17} strokeWidth={1.8} />
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Sök på namn, druva, region…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '11px 14px 11px 38px',
              border: '1px solid rgba(139,42,62,.16)',
              borderRadius: 24,
              fontSize: 13.5, background: '#fff',
              color: 'var(--ink)', outline: 'none',
              fontFamily: 'inherit',
              boxShadow: '0 1px 3px rgba(60,20,30,.05)',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)', padding: 0, lineHeight: 1 }}>×</button>
          )}
        </div>

        {/* Slumpa */}
        {visibleWines.length > 0 && (
          <button
            onClick={() => {
              const pool = sorted.length > 0 ? sorted : visibleWines;
              openDetail(pool[Math.floor(Math.random() * pool.length)]);
            }}
            style={{
              width: '100%', marginBottom: 10,
              background: 'var(--wine-light)', border: '1px solid rgba(139,42,62,.14)',
              borderRadius: 12, padding: '11px 14px',
              fontSize: 13.5, color: 'var(--wine)', fontWeight: 500,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9,
              fontFamily: 'inherit',
            }}
          >
            <Shuffle size={17} strokeWidth={1.8} />
            Slumpa ett vin
            {filtered.length !== visibleWines.length && (
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>bland {filtered.length} viner</span>
            )}
          </button>
        )}

        {/* Filter + typ-chips */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setShowFilters(v => !v)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: activeCount > 0 ? 'var(--wine-light)' : '#fff',
                border: `1px solid ${activeCount > 0 ? 'var(--wine)' : 'rgba(139,42,62,.16)'}`,
                borderRadius: 12, padding: '10px 14px',
                fontSize: 13.5, color: activeCount > 0 ? 'var(--wine)' : 'var(--muted)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Filter size={16} strokeWidth={1.8} />
                Filter & sortering{activeCount > 0 ? ` (${activeCount})` : ''}
              </span>
              <ChevronDown size={15} strokeWidth={1.8} style={{ transform: showFilters ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            {anyFilter && (
              <button
                onClick={() => { setTypeFilter('Alla'); setHostFilter('Alla'); setMinRating(0); setPriceRange('Alla'); setSbFilter('Alla'); setUserFilter([]); setSortBy('date_desc'); }}
                style={{ background: 'none', border: '1px solid rgba(139,42,62,.16)', borderRadius: 12, padding: '10px 12px', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Rensa
              </button>
            )}
          </div>

          {/* Kollapsbart filterpanel */}
          {showFilters && (
            <div style={{ background: 'white', border: '1px solid rgba(60,20,30,.08)', borderRadius: 16, padding: '12px 14px', marginTop: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Sortering</div>
              <div className="filter-row" style={{ marginBottom: 12 }}>
                {[['date_desc','Datum ↓'],['name_asc','Namn A–Ö'],['price_asc','Pris ↑'],['price_desc','Pris ↓'],['rating_desc','Betyg ↓'],['rating_asc','Betyg ↑']].map(([val, label]) => (
                  <div key={val} className={`filter-chip ${sortBy === val ? 'active' : ''}`} onClick={() => setSortBy(val)}>{label}</div>
                ))}
              </div>

              {hosts.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Värd</div>
                  <div className="filter-row" style={{ marginBottom: 12 }}>
                    {['Alla', ...hosts].map(f => (
                      <div key={f} className={`filter-chip ${hostFilter === f ? 'active' : ''}`} onClick={() => setHostFilter(f)}>{f}</div>
                    ))}
                  </div>
                </>
              )}

              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Minimibetyg</div>
              <div className="filter-row" style={{ marginBottom: 12 }}>
                {[[0,'Alla'],[3,'3.0+'],[3.5,'3.5+'],[4,'4.0+'],[4.5,'4.5+']].map(([val, label]) => (
                  <div key={label} className={`filter-chip ${minRating === val ? 'active' : ''}`} onClick={() => setMinRating(val)}>{label}</div>
                ))}
              </div>

              {hasAnyPrice && (
                <>
                  <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Pris</div>
                  <div className="filter-row" style={{ marginBottom: 12 }}>
                    {[['Alla','Alla'],['<100','Under 100 kr'],['100-200','100–200 kr'],['200-300','200–300 kr'],['300+','300+ kr']].map(([val, label]) => (
                      <div key={val} className={`filter-chip ${priceRange === val ? 'active' : ''}`} onClick={() => setPriceRange(val)}>{label}</div>
                    ))}
                  </div>
                </>
              )}

              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Systembolaget</div>
              <div className="filter-row" style={{ marginBottom: 12 }}>
                {[['Alla','Alla'],['Ja','Finns'],['Nej','Finns inte']].map(([val, label]) => (
                  <div key={val} className={`filter-chip ${sbFilter === val ? 'active' : ''}`} onClick={() => setSbFilter(val)}>{label}</div>
                ))}
              </div>

              {userProfiles.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Användare</div>
                  <div className="filter-row">
                    {userProfiles.map(({ id, name }) => (
                      <div
                        key={id}
                        className={`filter-chip ${userFilter.includes(id) ? 'active' : ''}`}
                        onClick={() => setUserFilter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Typ-chips – alltid synliga */}
          <div style={{ display: 'flex', gap: 7, overflowX: 'auto', padding: '10px 16px 2px', margin: '0 -16px', scrollbarWidth: 'none' }}>
            {['Alla', ...types].map(f => {
              const tm = TYPE[f];
              const isActive = typeFilter === f;
              return (
                <span
                  key={f}
                  onClick={() => setTypeFilter(f)}
                  style={{
                    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: isActive ? 'var(--wine)' : '#fff',
                    color: isActive ? '#fff' : 'var(--ink)',
                    border: `1px solid ${isActive ? 'var(--wine)' : 'rgba(139,42,62,.16)'}`,
                    fontSize: 12, padding: '7px 14px', borderRadius: 22, whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  {!isActive && tm && (
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: tm.dot, flexShrink: 0 }} />
                  )}
                  {tm ? tm.label : f}
                </span>
              );
            })}
          </div>
        </div>

        {/* Tomt tillstånd */}
        {!filtered.length && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
            {search
              ? <Search size={32} color="rgba(60,20,30,.2)" strokeWidth={1.5} style={{ marginBottom: 10 }} />
              : <Wine size={32} color="rgba(60,20,30,.2)" strokeWidth={1.5} style={{ marginBottom: 10 }} />
            }
            <div style={{ fontSize: 14 }}>{search ? `Inga viner matchar "${search}"` : 'Inga viner än.'}</div>
            {search && (
              <button onClick={() => setSearch('')} style={{ marginTop: 12, fontSize: 13, color: 'var(--wine)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Rensa sökning
              </button>
            )}
          </div>
        )}

        {/* Vinkort */}
        {sorted.map(w => {
          const a = avg(w.ratings);
          const tm = TYPE[w.type] || { dot: '#8B2A3E', bg: '#F5E8EB', fg: '#5C1A28', label: w.type };
          const ratingNum = parseFloat(a) || 0;
          const note = w.profile || w.ratings?.find(r => r.comment?.trim())?.comment;
          const times = w.use_count || 1;
          const isInterested = w.interests?.some(i => i.user_id === profile.id);
          const src = getWineSource(w);

          return (
            <div key={w.id} style={{
              background: tm.bg,
              borderRadius: 18,
              padding: 15,
              marginBottom: 12,
              border: '1px solid rgba(60,20,30,.08)',
              boxShadow: '0 1px 3px rgba(60,20,30,.05)',
              cursor: 'pointer',
            }} onClick={() => openDetail(w)}>

              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                {/* Betygsmedaljong */}
                <div style={{
                  flexShrink: 0, width: 58, height: 58, borderRadius: '50%',
                  background: '#fff', border: `2px solid ${tm.dot}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                  {ratingNum > 0 ? (
                    <>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 21, fontWeight: 700, color: tm.fg, lineHeight: .9 }}>{a}</div>
                      <div style={{ fontSize: 7.5, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)', marginTop: 2 }}>av 5</div>
                    </>
                  ) : (
                    <div style={{ fontSize: 9, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--muted)', textAlign: 'center', lineHeight: 1.3 }}>Ej<br />betyg</div>
                  )}
                </div>

                {/* Vininfo */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 600, lineHeight: 1.12, color: 'var(--ink)' }}>{w.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{w.grape}{w.vintage ? ` · ${w.vintage}` : ''}</div>
                  {(w.region || w.country) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
                      <MapPin size={12} strokeWidth={1.9} />
                      {[w.region, w.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    {ratingNum > 0 && <Stars rating={ratingNum} size={12} />}
                    {times > 1 && <span style={{ fontSize: 10, color: 'var(--muted)' }}>Provats {times}×</span>}
                  </div>
                </div>
              </div>

              {/* Smaknot */}
              {note && (() => {
                const LIMIT = 120;
                const isLong = note.length > LIMIT;
                const isExpanded = expandedNotes.has(w.id);
                const displayNote = isLong && !isExpanded ? note.slice(0, LIMIT).trimEnd() + '…' : note;
                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 14, color: 'var(--ink)', lineHeight: 1.45, opacity: .85 }}>
                      "{displayNote}"
                    </div>
                    {isLong && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setExpandedNotes(prev => {
                            const next = new Set(prev);
                            next.has(w.id) ? next.delete(w.id) : next.add(w.id);
                            return next;
                          });
                        }}
                        style={{ marginTop: 5, fontSize: 12, color: tm.fg, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontWeight: 500 }}
                      >
                        {isExpanded ? 'Visa mindre ↑' : 'Visa mer ↓'}
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Footer */}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {w.interests?.slice(0, 5).map(i => (
                    <div key={i.id} style={{
                      width: 24, height: 24, borderRadius: '50%', background: '#fff',
                      border: `1.5px solid ${tm.bg}`, boxShadow: '0 0 0 1px rgba(60,20,30,.06)',
                      marginRight: -7, overflow: 'hidden', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 600, color: tm.fg,
                    }}>
                      {i.profile?.avatar_url
                        ? <img src={i.profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : i.profile?.display_name?.[0]
                      }
                    </div>
                  ))}
                  {w.interests?.length > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 14 }}>{w.interests.length} intresserade</span>
                  )}
                </div>
                <button onClick={e => toggleInterest(e, w.id)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, fontWeight: 500,
                  background: '#fff', border: 'none',
                  color: isInterested ? 'var(--wine)' : tm.fg,
                  cursor: 'pointer', padding: '6px 13px', borderRadius: 20,
                  fontFamily: 'inherit',
                }}>
                  <Heart size={14} strokeWidth={1.8} fill={isInterested ? 'currentColor' : 'none'} />
                  Intresse
                </button>
              </div>

              {/* Källa */}
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 500, color: tm.fg, opacity: .8 }}>{src.label}</span>
                {src.date && <span>· {src.date}</span>}
                {src.addedBy.length > 0 && (
                  <>
                    <span>· av</span>
                    {src.addedBy.map((name, i) => {
                      const hostProfile = Object.entries(profiles).find(([, n]) => n === name);
                      const hostId = hostProfile?.[0];
                      return (
                        <span
                          key={i}
                          style={{ color: 'var(--wine)', fontWeight: 500, cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); onViewMember?.(hostId); }}
                        >
                          {name}{i < src.addedBy.length - 1 ? ' &' : ''}
                        </span>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button className="fab" onClick={() => setShowModal(true)}>
        <Plus size={20} strokeWidth={2} />
        Nytt vin
      </button>

      {showModal && (
        <WineModal
          profile={profile}
          wines={visibleWines}
          sessionId={sessionId}
          onClose={() => { setShowModal(false); setSessionId(null); }}
          onSaved={() => { setShowModal(false); setSessionId(null); fetchWines(); }}
        />
      )}
    </div>
  );
}
